import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EyeIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import ProductSkeleton from './product-skeleton';
import DEFAULT_IMAGE from '../../../assets/images/default-product.jpg';
import useCart from '../../../hooks/use-cart';
import { Product } from '../../../types/retail-product';
import Spinner from '../../../components/spinner';
import StarRating from '../../../components/star-rating';
import RiyalSymbol from '../../../assets/svg/RiyalSymbol';

export default function ProductCard({ data }: { data: Product }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showShimmer, setShowShimmer] = useState(true);
  const { addItemToCart, isAddingToCart } = useCart();
  const [isAdded, setIsAdded] = useState(false);
  const imageUrl = data?.attributes?.logo?.url || DEFAULT_IMAGE;
  const { locale } = useParams();
  const addToCartButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (data?.id && !isAddingToCart) {
      if (addToCartButtonRef.current) {
        const rect = addToCartButtonRef.current.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };

        window.dispatchEvent(new CustomEvent('addToCartAnimation', {
          detail: { position },
        }));
      }

      addItemToCart(data.id, 1);
      setIsAdded(true);
      setTimeout(() => setIsAdded(false), 2000);
    }
  };

  useEffect(() => {
    const shimmerTimeout = setTimeout(() => {
      if (!imageLoaded) {
        setShowShimmer(true);
      }
    }, 300);

    return () => clearTimeout(shimmerTimeout);
  }, [imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setShowShimmer(false);
  };

  if (showShimmer) {
    return (
      <ProductSkeleton>
        <img
          alt={data?.attributes?.name || 'Product image'}
          src={imageUrl}
          className='hidden'
          onLoad={handleImageLoad}
          onError={handleImageLoad}
        />
      </ProductSkeleton>
    );
  }

  const hasDiscount = data?.attributes?.discount_percentage && data?.attributes?.discount_percentage > 0;
  const hasCustomTag = data?.attributes?.tag && data?.attributes?.tag_color !== '#000000';

  return (
    <Link to={`/${locale}/product/${data.attributes.slug}`} className='group block max-w-xs w-full'>
      <div className='bg-white rounded-xl border border-gray-200/80 overflow-hidden font-sans transition-all duration-300 ease-in-out hover:border-gray-300 hover:-translate-y-1'>
        <div className='relative h-64 overflow-hidden'>
          {hasCustomTag && (
            <div
              className='absolute top-2 left-2 z-10 text-white text-xs font-semibold py-1 px-3 rounded-md'
              style={{ backgroundColor: data.attributes.tag_color }}
            >
              {data.attributes.tag}
            </div>
          )}

          <img
            className='w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-110'
            src={imageUrl}
            alt={data?.attributes?.name || 'Product Name'}
            onLoad={handleImageLoad}
            onError={handleImageLoad}
          />

          <div className='absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
            <EyeIcon className='h-12 w-12 text-white' />
          </div>
        </div>
        <div className='p-3'>
          <h3 className='text-md font-semibold text-gray-800 truncate mb-2' title={data?.attributes?.name}>
            {data?.attributes?.name || 'Product Name'}
          </h3>

          <div>
            <div className='flex items-baseline gap-1'>
              {hasDiscount ? (
                <>
                  <p className='text-2xl font-bold text-gray-900 flex items-center' dir='ltr'>
                    <RiyalSymbol color='currentColor' width={16} height={16} />
                    <span>{data.attributes.base_price_with_vat}</span>
                  </p>
                  <p className='relative inline-flex items-center text-sm text-gray-500' dir='ltr'>
                    <span className='flex items-center'>
                      <RiyalSymbol color='currentColor' width={12} height={12} />
                      <span>{data.attributes.marketing_price}</span>
                    </span>
                    <span className='absolute left-0 right-0 top-1/2 border-t border-gray-500' />
                  </p>
                </>
              ) : (
                <p className='text-xl font-bold text-gray-900 flex items-center gap-1' dir='ltr'>
                  <RiyalSymbol color='currentColor' width={16} height={16} />
                  <span>{data?.attributes?.base_price_with_vat ?? 'N/A'}</span>
                </p>
              )}
            </div>

            <div className='mt-1 min-h-[22px]'>
              {hasDiscount && (
                <span className='inline-block bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-md'>
                  {t('shared.savePercentage', { percentage: data?.attributes?.discount_percentage })}
                </span>
              )}
            </div>
          </div>

          <div className='flex justify-between items-center mt-2'>
            <StarRating rating={data.attributes.statistics.reviews_average} count={data.attributes.statistics.reviews_count} className='h-5 w-5' />
            <button
              type='button'
              ref={addToCartButtonRef}
              onClick={handleAddToCart}
              disabled={isAddingToCart || !data.attributes.in_stock || isAdded}
              className={`group/button relative flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ease-in-out transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isAdded
                  ? 'bg-white border border-[#80BC00] focus:ring-[#80BC00] cursor-default'
                  : !data.attributes.in_stock
                    ? 'bg-gray-300 cursor-not-allowed'
                    : isAddingToCart
                      ? 'bg-[#CCE499] cursor-wait'
                      : 'bg-[#80BC00] hover:bg-opacity-90 focus:ring-[#80BC00]'
              }`}
            >
              {isAdded ? (
                <svg className='w-6 h-6 text-[#80BC00]' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>
              ) : isAddingToCart ? (
                <Spinner />
              ) : !data.attributes.in_stock ? (
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>
              ) : (
                <>
                  <span className='transition-opacity duration-300 group-hover/button:opacity-0'>
                    <ShoppingCartIcon className='w-6 h-6 text-white' />
                  </span>
                  <span className='absolute opacity-0 transition-opacity duration-300 group-hover/button:opacity-100'>
                    <ShoppingCartIcon className='w-6 h-6 text-white' />
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
